// apps/scheduler-worker/src/pages/TasksPage.tsx
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { TaskCard } from '../components/TaskCard';
import { EmptyState } from '../components/EmptyState';
import { TaskListSkeleton } from '../components/LoadingSkeleton';
import { useTasks } from '../hooks/useTasks';
import { Button } from 'scheduler-ui';

export function TasksPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useTasks();

  if (isLoading) {
    return <TaskListSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700 mb-2">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Failed to load tasks</span>
          </div>
          <p className="text-sm text-red-600 mb-3">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const tasks = data?.data ?? [];
  const activeTaskId = data?.activeTask?.id;

  if (tasks.length === 0) {
    return (
      <EmptyState
        title="No tasks assigned"
        description="Check back later or contact your planner."
      />
    );
  }

  return (
    <div className="p-4 space-y-3">
      {/* Active task section */}
      {activeTaskId && (
        <div className="mb-4">
          <h2 className="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">
            Currently Working
          </h2>
          {tasks
            .filter((t) => t.id === activeTaskId)
            .map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                isActive
                onClick={() => navigate(`/tasks/${task.id}`)}
              />
            ))}
        </div>
      )}

      {/* Task queue */}
      <div>
        <h2 className="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">
          {activeTaskId ? 'Up Next' : 'Your Tasks'}
        </h2>
        <div className="space-y-3">
          {tasks
            .filter((t) => t.id !== activeTaskId)
            .map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={() => navigate(`/tasks/${task.id}`)}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
